// Pill — a small status pill: "Settled", "Awaiting", "Carried in", "Closed",
// "Not started", "Measured", "No cost set".
//
// Item 70: Periods, Dashboard, Inventory, Section (Badge) and DealSlideOver
// each kept their own, two of them on off-palette colours (green-100,
// amber-800) that the console never defined.
//
// THE RULE IT ENFORCES (§7 rule 10 — "no colour carries a meaning on its
// own"): `children` is a STRING. A pill always says its state in a word;
// the tone only tints it. An icon may sit beside the word, never instead of
// it. Tones map to the four palette tokens and nothing else. Which tone a
// state wears is the emphasis rule in src/styles.css (awaiting / pending =
// warn, settled = good, voided = bad, carried in = neutral).
//
// Usage:
//   <Pill tone="good">Settled</Pill>
//   <Pill tone="warn">Awaiting</Pill>
//   <Pill tone="bad">Voided</Pill>
//   <Pill>Carried in</Pill>                       // neutral
//   <Pill tone="good" icon={<Lock size={11} />}>Closed</Pill>

import type { ReactNode } from 'react';

export type PillTone = 'neutral' | 'good' | 'warn' | 'bad' | 'info' | 'gold';

const TONE: Record<PillTone, string> = {
  neutral: 'bg-panel-2 text-tg-text',
  good:    'bg-pos-bg text-pos',
  warn:    'bg-warn-bg text-warn',
  bad:     'bg-neg-bg text-neg',
  info:    'bg-info-bg text-info',          // "Your figure" on Inventory
  gold:    'bg-brand-gold/10 text-brand-gold', // a cheque's kind pill
};

/** Two sizes and no more: 'sm' for a status pill beside 14px text, 'md' for
 *  a type pill inside a 13.5px ledger row (components/Ledger's KindPill),
 *  where 11.5px would be the smallest thing on the line. */
const SIZE = { sm: 'text-meta', md: 'text-meta' } as const;

export function Pill({ tone = 'neutral', size = 'sm', icon, children, className = '' }: {
  tone?: PillTone;
  size?: keyof typeof SIZE;
  icon?: ReactNode;
  /** The state, as a word. Never empty, never an icon alone. */
  children: string;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 ${SIZE[size]} font-medium whitespace-nowrap ${TONE[tone]} ${className}`}>
      {icon && <span className="shrink-0 opacity-80">{icon}</span>}
      {children}
    </span>
  );
}
